import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getPhaseById, getPhaseStepsById, getSteps } from "../../../../../api/v2/PhasesApi";
import { downloadFileWithName } from "../../../../../api/v2/FilesApi";
import { downloadFileByLink } from "../../../fileDownload";
import { getStep, updateStep } from "../../../../../api/v2/StepsApi";

import { CommentDto } from "../../../../../dtos/CommentDto";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { PhaseStatus } from "../../../../../dtos/PhaseStatus";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import { StepDto } from "../../../../../dtos/StepDto";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { StepWorkflowType } from "../../../../../dtos/StepWorkflowType";

import { useAppContext } from "../../../AppContext";
import { isTemplatePhase } from "../../../Types";
import { checkStepPermission, deriveStepStyleTaskStatus, getEffectiveStepType, getPhaseStatusInfo, isStepStyleWorkflow, isUploadOnlyPhase } from "../../../Utils";
import { useTaskListContext } from "../../context/TaskListContext";
import { ITaskSummaryProps } from "../TaskSummary";
import { ITaskSummaryViewProps } from "../components/TaskSummaryView";
import { cycleStatusFor, isClosedCycle } from "../taskCycle";

export function useTaskSummaryController({
    phase,
    grantNumber,
    grantData,
    hideSummary,
    onActionSelected,
    onLoading,
    onLoaded,
    onClose,
}: ITaskSummaryProps): ITaskSummaryViewProps {
    const app = useAppContext();
    const taskListContext = useTaskListContext();

    const gridRef = useRef<any>(null);

    // Read the load callbacks through refs. Callers pass them as inline arrows, so keeping them in
    // the DataSource dependencies rebuilt it on every render - and the reload effect below then
    // refetched, re-rendered, and rebuilt it again (a self-feeding refresh loop).
    const onLoadingRef = useRef(onLoading);
    const onLoadedRef = useRef(onLoaded);
    onLoadingRef.current = onLoading;
    onLoadedRef.current = onLoaded;
    // Null until the first load finishes. A Step-style task's header status is derived from
    // these, so an empty starting value would read "Not Started" until they arrive.
    const [steps, setSteps] = useState<StepDto[] | null>(null);
    const [loadedPhase, setLoadedPhase] = useState<PhaseDto>(phase);

    // Selected resubmission cycle (null = latest). Mirrored to a ref so the grid's load()
    // reads the current cycle on reload without recreating the DataSource.
    const selectedSubmissionId = taskListContext.selectedSubmissionId;
    const submissionIdRef = useRef<string | null>(selectedSubmissionId);
    submissionIdRef.current = selectedSubmissionId;

    // keep local copy synced to prop
    useEffect(() => {
        setLoadedPhase(phase);
    }, [phase]);

    // Any tasklist refresh reloads this phase too. A cycle started from the task header only
    // calls refreshSteps, so without this the open summary kept the phase as it was before the
    // repeat - old status, old actions - until it was closed and reopened.
    const phaseIdRef = useRef(phase.id);
    phaseIdRef.current = phase.id;
    useEffect(() => {
        if (taskListContext.tasklistMode === "templates") return;
        return taskListContext.registerForRefreshSteps(async () => {
            const updated = await getPhaseById(phaseIdRef.current);
            if (updated?.data) setLoadedPhase(updated.data);
        });
    }, [taskListContext.registerForRefreshSteps, taskListContext.tasklistMode]);

    // Filter subtasks by View permission, matching the tasklist. Template mode shows all.
    const filterVisibleSteps = useCallback(
        (list: StepDto[]) => {
            if (taskListContext.tasklistMode === "templates" || isTemplatePhase(loadedPhase)) return list;
            return list.filter(step => checkStepPermission(step, app, PermissionType.View, true, loadedPhase, grantData?.id));
        },
        [taskListContext.tasklistMode, app, loadedPhase, grantData?.id]
    );

    // fetch steps list shown in the summary, honoring the selected resubmission cycle
    // (null = latest). phaseSubmissions[0] is the latest cycle.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const subs = loadedPhase?.phaseSubmissions ?? [];
            const subId =
                (selectedSubmissionId ? subs.find(s => s.id === selectedSubmissionId)?.id : null)
                ?? subs[0]?.id;
            if (!subId) {
                setSteps([]);
                return;
            }
            const resp = await getSteps(subId);
            if (cancelled) return;
            if (resp?.error) app.errorToast(resp.error.message);
            else setSteps(filterVisibleSteps(resp?.data ?? []));
        })();
        return () => {
            cancelled = true;
        };
    }, [loadedPhase, app, selectedSubmissionId, filterVisibleSteps]);

    // DataSource backing the grid (returns StepDto rows). No setState in load.
    const dataSource = useMemo(
        () =>
            new DataSource<StepDto, string>({
                paginate: false,
                requireTotalCount: true,
                store: new CustomStore<StepDto, string>({
                    key: "id",
                    load: async () => {

                        onLoadingRef.current?.();

                        let steps: StepDto[] = [];

                        if (isTemplatePhase(loadedPhase)) {
                            steps = loadedPhase.steps;
                        }
                        else {
                            // Default to the latest cycle (getPhaseStepsById); when a prior
                            // resubmission cycle is selected, load that cycle's steps instead.
                            const subs = loadedPhase?.phaseSubmissions ?? [];
                            const cycleId = submissionIdRef.current;
                            const targetSub = cycleId
                                ? subs.find(s => s.id === cycleId)
                                : null;
                            const result = targetSub
                                ? await getSteps(targetSub.id)
                                : await getPhaseStepsById(loadedPhase.id);
                            if (result?.error) {
                                app.errorToast(result.error.message);
                            }
                            steps = result?.data ?? [];
                        }

                        steps = filterVisibleSteps(steps);

                        onLoadedRef.current?.();

                        return { data: steps, totalCount: steps.length };
                    },
                }),
            }),
        [loadedPhase, app, filterVisibleSteps]
    );

    // Reload the summary grid when the viewed cycle changes (ref is already current).
    useEffect(() => {
        dataSource.reload();
        gridRef.current?.instance?.refresh?.();
    }, [selectedSubmissionId, dataSource]);

    const closeSummary = useCallback(() => {
        onClose();
    }, [onClose]);

    const download = useCallback(
        async (fileId: string, fileName: string) => {
            app.showLoadPanel();

            // The browser opens a signed link and streams the file itself.
            if (app.configuration?.streamingFileTransfer) {
                const error = await downloadFileByLink(fileId, fileName);
                app.hideLoadPanel();
                if (error) app.errorToast(error);
                return;
            }

            // Use the server's presentation name (FY/step prefix for Submission, plain stored
            // name otherwise); fall back to the stored name if the header is missing.
            const result = await downloadFileWithName(fileId);

            if (result?.error) {
                app.errorToast(result.error.message);
            } else {
                const blob = result?.data?.blob;
                const downloadName = result?.data?.fileName || fileName;
                if (blob) {
                    const link = document.createElement("a");
                    const url = URL.createObjectURL(blob);
                    link.href = url;
                    link.download = downloadName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                } else {
                    app.errorToast(`Failed to download: ${fileName}`);
                }
            }
            app.hideLoadPanel();
        },
        [app]
    );

    const getMostRecentComment = useCallback((comments: CommentDto[]) => {
        if (!Array.isArray(comments) || comments.length === 0) return null;
        const mostRecent = comments.reduce((latest, current) =>
            new Date(current.dateCreated as Date) > new Date(latest.dateCreated as Date) ? current : latest
        );
        return mostRecent.comment || null;
    }, []);

    const buildFileLink = useCallback(
        (file: { id: string; name: string }) => ({
            id: file.id,
            name: file.name,
            onClick: () => { void download(file.id, file.name); },
        }),
        [download]
    );

    const selectAction = useCallback(async () => {
        const updatedPhase = await getPhaseById(loadedPhase.id);
        if (updatedPhase?.error) {
            app.errorToast(updatedPhase.error.message);
            return;
        }

        setLoadedPhase(updatedPhase?.data as PhaseDto);
        taskListContext.refreshSteps();
        gridRef.current?.instance?.refresh?.();
        onActionSelected?.();
    }, [loadedPhase.id, app, taskListContext, onActionSelected]);

    // None shows the description/files block, not request/response.
    const isNoneWorkflow = useMemo(
        () => loadedPhase?.defaultPhase?.settings?.workflowType === PhaseWorkflowType.None,
        [loadedPhase?.defaultPhase?.settings?.workflowType]
    );

    // Step-style tasks (Step or StepHybrid) have no overall task status — subtasks run
    // independently, so the task itself is just a passive container.
    const isStepWorkflow = useMemo(
        () => isStepStyleWorkflow(loadedPhase?.defaultPhase?.settings?.workflowType),
        [loadedPhase?.defaultPhase?.settings?.workflowType]
    );

    // Upload-only tasks are file-only: the summary shows the files block but NOT the
    // description/completion row that other None tasks (Bidding/Construction) show.
    const uploadOnly = useMemo(
        () => isUploadOnlyPhase(loadedPhase),
        [loadedPhase?.defaultPhase?.settings?.uploadOnlySubtasks]
    );

    const showResponseInfo = useMemo(
        () => isStepWorkflow || (loadedPhase.status >= PhaseStatus.GRANTEE_PENDING && loadedPhase.status <= PhaseStatus.APPROVED),
        [isStepWorkflow, loadedPhase.status]
    );

    // A task with the extended header shows its name and dates in that row, so the summary title
    // just says "Summary" rather than repeating them.
    const hasExtendedHeader = loadedPhase?.defaultPhase?.settings?.extendedHeader === true;

    const headerTitle = useMemo(() => {
        let phaseName = hasExtendedHeader ? "Summary" : `Summary View for ${loadedPhase.name}`;
        // Step-style tasks have no task-level workflow, so the overall status is derived from
        // their subtasks - the same rule the tasklist toolbar indicator uses. Until those load
        // there is nothing to derive from, so the title carries no status.
        const stepStatusKnown = !isStepWorkflow || steps != null;
        if (taskListContext.tasklistMode !== "templates" && stepStatusKnown) {
            // The cycle in view, not the task overall - a closed cycle keeps the status its own
            // subtasks imply rather than inheriting the active cycle's (ticket 1023).
            const status = isStepWorkflow
                ? deriveStepStyleTaskStatus(steps!)
                : cycleStatusFor(loadedPhase, selectedSubmissionId) ?? loadedPhase.status;
            const info = getPhaseStatusInfo(
                status,
                loadedPhase.dueDate,
                loadedPhase?.defaultPhase?.settings?.workflowType);
            phaseName = phaseName + ` (${info.text})`;
        }

        // Add tracked dates to header
        if (!hasExtendedHeader && taskListContext.tasklistMode !== "templates" && loadedPhase?.defaultPhase?.settings?.tracked) {
            const startDate = loadedPhase.startDate
                ? new Date(loadedPhase.startDate).toLocaleDateString()
                : "Not set";
            const endDate = loadedPhase.projectedEndDate
                ? new Date(loadedPhase.projectedEndDate).toLocaleDateString()
                : "Not set";
            phaseName = `${phaseName}<br/><span style="font-size: smaller;"><strong>Start Date:</strong> ${startDate} <strong>Projected End Date:</strong> ${endDate}</span>`;
        }

        return phaseName;
    }, [selectedSubmissionId, loadedPhase, taskListContext.tasklistMode, hasExtendedHeader, uploadOnly, isStepWorkflow, steps]);

    // Whether a subtask's section takes the None shape (description + files) rather than
    // request/response. Decided per subtask, not per task: under a hybrid task each subtask has
    // its own type, so a None subtask beside Step and Phase ones still gets its files listed.
    const isNoneStep = useCallback(
        (step: StepDto) =>
            !!loadedPhase?.defaultPhase
            && getEffectiveStepType(loadedPhase.defaultPhase, step.defaultStep ?? null) === StepWorkflowType.None,
        [loadedPhase?.defaultPhase]
    );

    // Check if dates can be edited for a specific step using permission system
    const canEditStepDates = useCallback((step: StepDto): boolean => {
        // Must be a tracked phase
        if (!loadedPhase?.defaultPhase?.settings?.tracked) return false;

        // Step must not be complete
        if (step.status === StepStatus.GRANTEE_COMPLETE || step.status === StepStatus.APPROVED) {
            return false;
        }

        // Check EditDates permission for this step
        return checkStepPermission(step, app, PermissionType.EditDates, true, loadedPhase, grantData?.id);
    }, [loadedPhase, app, grantData]);

    // Date change handler for subtasks
    const onDateChange = useCallback(
        async (stepId: string, field: 'startDate' | 'projectedEndDate' | 'submissionDate', value: Date | null) => {
            // Guard against undefined values (can happen during component refresh)
            if (value === undefined) {
                return;
            }

            // Fetch the current step from the API to ensure we have the latest data
            const currentStepResult = await getStep(stepId);

            if (currentStepResult?.error) {
                app.errorToast(currentStepResult.error.message);
                return;
            }

            const currentStep = currentStepResult?.data;
            if (!currentStep) {
                app.errorToast('Step not found');
                return;
            }

            // Check if the date has actually changed before saving
            const currentValue = field === 'startDate'
                ? currentStep.startDate
                : field === 'projectedEndDate'
                    ? currentStep.projectedEndDate
                    : currentStep.submissionDate;
            const currentDate = currentValue ? new Date(currentValue).setHours(0, 0, 0, 0) : null;
            const newDate = value ? new Date(value).setHours(0, 0, 0, 0) : null;

            if (currentDate === newDate) {
                return;
            }

            // Create payload with only the fields that should be updated
            // Avoid sending nested objects (comments, files) as they can cause issues
            const updatedStep: StepDto = {
                id: currentStep.id,
                phaseSubmissionId: currentStep.phaseSubmissionId,
                name: currentStep.name,
                description: currentStep.description,
                spSyncDate: currentStep.spSyncDate,
                spId: currentStep.spId,
                spPath: currentStep.spPath,
                status: currentStep.status,
                availableStepStatuses: currentStep.availableStepStatuses,
                owner: currentStep.owner,
                dueDate: currentStep.dueDate,
                completionDate: currentStep.completionDate,
                defaultStep: currentStep.defaultStep,
                order: currentStep.order,
                latestComment: currentStep.latestComment,
                requestComment: currentStep.requestComment,
                responseComment: currentStep.responseComment,
                revisionRequestComments: currentStep.revisionRequestComments,
                dateCreated: currentStep.dateCreated,
                dateUpdated: currentStep.dateUpdated,
                files: currentStep.files,
                // Update the specific date field
                startDate: field === 'startDate' ? value : currentStep.startDate,
                projectedEndDate: field === 'projectedEndDate' ? value : currentStep.projectedEndDate,
                submissionDate: field === 'submissionDate' ? value : currentStep.submissionDate,
            };

            const result = await updateStep(stepId, updatedStep);

            if (result?.error) {
                app.errorToast(result.error.message);
            } else if (result?.data) {
                // Verify the date was actually saved correctly
                const savedValue = field === 'startDate'
                    ? result.data.startDate
                    : field === 'projectedEndDate'
                        ? result.data.projectedEndDate
                        : result.data.submissionDate;
                const expectedValue = value;

                // Compare dates (handle null and convert to comparable format)
                // Need to compare only the date part, ignoring time
                const savedDate = savedValue ? new Date(savedValue).setHours(0, 0, 0, 0) : null;
                const expectedDate = expectedValue ? new Date(expectedValue).setHours(0, 0, 0, 0) : null;

                if (savedDate !== expectedDate) {
                    app.errorToast(`Warning: ${field} may not have been saved correctly. Expected: ${expectedValue?.toLocaleDateString()}, Got: ${savedValue ? new Date(savedValue).toLocaleDateString() : 'null'}`);
                }

                // Refresh the steps list with the actual saved data
                const updatedSteps = steps?.map(s => s.id === stepId ? result.data! : s) ?? [];
                setSteps(updatedSteps);
                gridRef.current?.instance?.refresh?.();

                // refreshSteps only reloads the list - it does not touch selectedStep. The detail
                // panel binds to selectedStep and posts the whole object back on save, so leaving a
                // pre-edit copy there lets a later save overwrite what was just stored here (the
                // dates saved one field at a time, so it could even hold a half-updated snapshot).
                if (taskListContext.getSelectedStep()?.id === stepId) {
                    taskListContext.setSelectedStep(result.data);
                }

                taskListContext.refreshSteps();
            }
        },
        [steps, app, taskListContext]
    );

    // A closed cycle's summary is history: no editable dates and no task actions. Needed on top of
    // the server guard because this view loads its phase through GetPhase, which always resolves the
    // newest cycle - so phaseActions describes the open cycle even while an older one is displayed
    // (ticket 1023).
    const isCycleClosed = isClosedCycle(loadedPhase, selectedSubmissionId);

    return {
        // state/refs
        gridRef,
        hideSummary,
        isCycleClosed,

        // data
        loadedPhase,
        dataSource,
        steps,

        // behavior
        closeSummary,
        selectAction,

        // helpers for the View
        showResponseInfo,
        getMostRecentComment,
        buildFileLink,
        headerTitle,
        grantNumber,
        isPhaseTracked: loadedPhase?.defaultPhase?.settings?.tracked ?? false,
        trackedMode: loadedPhase?.defaultPhase?.settings?.trackedMode ?? undefined,
        canEditStepDates,
        onDateChange,
        minDate: loadedPhase?.startDate ? new Date(loadedPhase.startDate) : null,
        maxDate: loadedPhase?.projectedEndDate ? new Date(loadedPhase.projectedEndDate) : null,

        // Workflow flags
        isNoneWorkflow,
        isStepWorkflow,
        isUploadOnlyTask: uploadOnly,
        isNoneStep,
        mode: taskListContext.tasklistMode,
    };
}
